/*
 * The top of an integration card on Settings → Integrations: the logo, the name, one line
 * saying what it does, a "What we send" link, and the button on the right.
 *
 * Rob, 27/10/2026: *"lets tidy up … so at least it looks less chaotic with proper logos"*.
 * This is Timetastic's shape — layout A in `Claude outputs/easy-installs-mock-v1.html`.
 *
 * ⚠️ **THE LOGOS ARE THE BRANDS' OWN FILES, UNALTERED**, served from `public/integrations/`.
 * Slack's and Microsoft's brand rules both ask for permission before their marks are used;
 * Rob chose (27/10/2026, option 1a) to show them, as Timetastic do. So: never redraw,
 * recolour, crop or stretch one here — replace the file instead. Microsoft's rule is also
 * that the name reads "Microsoft Teams", never just "Teams", the first time it appears.
 *
 * ⚠️ **A MISSING FILE SHOWS A PLAIN ICON, NEVER A BROKEN IMAGE.** The files are added by
 * hand (nothing in the build fetches them), so a fresh checkout without them must still
 * look finished.
 */
import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

export const LOGOS = {
  slack: '/integrations/slack.png',
  teams: '/integrations/teams.png',
};

export function IntegrationLogo({ brand, fallback: Fallback }) {
  const [broken, setBroken] = useState(false);

  if (broken || !LOGOS[brand]) {
    return (
      <div className="w-10 h-10 shrink-0 rounded-lg bg-primary/20 flex items-center justify-center"
           data-testid={`integration-logo-fallback-${brand}`}>
        <Fallback className="w-5 h-5 text-primary" aria-hidden="true" />
      </div>
    );
  }

  return (
    /* ⚠️ A white tile in dark mode too: the marks are made for a light background. */
    <div className="w-10 h-10 shrink-0 rounded-lg border border-border bg-white flex items-center justify-center p-1.5">
      <img src={LOGOS[brand]} alt="" className="w-full h-full object-contain"
           onError={() => setBroken(true)} data-testid={`integration-logo-${brand}`} />
    </div>
  );
}

/*
 * What actually travels to Slack or Teams, in words an admin can repeat to their staff.
 * ⚠️ Checked against the code on 27/10/2026: `services/slack_cards.approval_blocks` and
 * `services/teams_cards` (one argument list for both), `notify()` for each person's own
 * messages, and `notifications_scheduler._whos_off` for the post (BUGS.md P2: a PRIVATE type
 * travels as "Time off"). If one of those starts sending something new, say it here too —
 * and in PRIVACY-BRIEF.md.
 */
export function whatWeSend(place) {
  return [
    `To an approver: the request — who, the leave type, the dates and how long, the reason in their own words, what their balance would be, and who else in their department is off then — with buttons to approve or decline.`,
    `To each person who chooses ${place}: their own notifications, the same ones we’d otherwise email.`,
    `In the channel you choose for the who’s-off post: names, leave types and dates, grouped by department. A private leave type shows as “Time off”.`,
    `Nothing goes to ${place} until somebody chooses it: each person picks ${place} on their own profile, and the who’s-off post is switched on in Settings → Notifications.`,
    `We keep a note of where each approval message went, so its buttons can be taken off once the request is settled — never what it said.`,
  ];
}

export default function IntegrationHeader({ brand, fallback, name, line, place, status, action }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="mb-4">
      <div className="flex flex-wrap sm:flex-nowrap items-start gap-3">
        <IntegrationLogo brand={brand} fallback={fallback} />
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-heading font-bold">{name}</h3>
            {status}
          </div>
          <p className="text-sm text-muted-foreground">{line}</p>
          <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open}
                  className="mt-1 inline-flex items-center gap-1 text-xs text-muted-foreground underline hover:text-foreground"
                  data-testid={`integration-sends-${brand}`}>
            What we send to {place}
            {open
              ? <ChevronUp className="w-3 h-3" aria-hidden="true" />
              : <ChevronDown className="w-3 h-3" aria-hidden="true" />}
          </button>
        </div>
        {/* ⚠️ On a phone the button drops under the words, lined up with them, not the logo. */}
        {action && <div className="shrink-0 w-full sm:w-auto pl-[52px] sm:pl-0">{action}</div>}
      </div>

      {open && (
        <ul className="mt-3 ml-[52px] space-y-1.5 text-xs text-muted-foreground list-disc pl-4"
            data-testid={`integration-sends-list-${brand}`}>
          {whatWeSend(place).map((item) => <li key={item}>{item}</li>)}
        </ul>
      )}
    </div>
  );
}
